/**
 * `/sessions/:id/door`: the household sheet — the door currently assigned
 * to the caller, the household's electors, the visit outcome, the star
 * rating, the household note, and the engagements taken with the person
 * (`docs/API.md` §4, §6, §8).
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ApiError } from '../../../api/client';
import {
  createEngagement,
  getCurrentAssignment,
  getSession,
  recordVisit,
} from '../../../api/household';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Skeleton,
  Textarea,
  useToast,
} from '../../../components/ui';
import { ConsentBox } from '../components/ConsentBox';
import {
  emptyEngagementDrafts,
  ENGAGEMENT_KINDS,
  EngagementList,
} from '../components/EngagementList';
import { HouseholdList } from '../components/HouseholdList';
import { OutcomeChoice } from '../components/OutcomeChoice';
import type { OutcomeChoiceOption } from '../components/OutcomeChoice';
import { StarRating } from '../components/StarRating';
import { formatShortDate } from '../format';
import { t } from '../../../i18n/fr';
import { OUTCOME_LABEL_KEYS } from '../labels';
import { PhantomBadge } from '../../session/components/PhantomBadge';

import type { EngagementDrafts } from '../components/EngagementList';
import type { RegistrationDoorNavigationState } from '../navigation';
import type { Assignment, ApiErrorBody, VisitOutcome } from '../../../api/types';

const OUTCOMES_REQUIRING_QUALITY: ReadonlySet<VisitOutcome> = new Set([
  'exchange',
  'registration_info',
]);

/**
 * Render the household sheet for the caller's currently assigned door.
 *
 * @returns The page element.
 */
export function HouseholdSheetPage() {
  const { id } = useParams<{ id: string }>();
  const sessionId = Number(id);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [assignment, setAssignment] = useState<Assignment | null | undefined>(undefined);
  const [loadError, setLoadError] = useState(false);

  const [outcome, setOutcome] = useState<VisitOutcome | null>(null);
  const [quality, setQuality] = useState<number | null>(null);
  const [doNotVisit, setDoNotVisit] = useState(false);
  const [note, setNote] = useState('');
  const [engagements, setEngagements] = useState<EngagementDrafts>(emptyEngagementDrafts());
  // The person who consents is not necessarily an elector of the household,
  // and never assumed to be: the volunteer asks and types the first name.
  const [engagementFirstName, setEngagementFirstName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const communeRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    setAssignment(undefined);
    setLoadError(false);
    getCurrentAssignment(sessionId)
      .then((result) => {
        if (!cancelled) setAssignment(result);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  if (loadError) {
    return (
      <Card>
        <p role="alert">{t('household.sheet.loadError')}</p>
      </Card>
    );
  }

  if (assignment === undefined) {
    return (
      <Card>
        <Skeleton height="1.5rem" />
        <Skeleton height="4rem" />
        <Skeleton height="4rem" />
      </Card>
    );
  }

  if (assignment === null) {
    return (
      <Card>
        <h1>{t('household.sheet.noAssignment.title')}</h1>
        <p>{t('household.sheet.noAssignment.body')}</p>
        <Link to={`/sessions/${sessionId}`}>{t('household.sheet.noAssignment.back')}</Link>
      </Card>
    );
  }

  const door = assignment.door;
  // Captured locally: TypeScript does not carry the `assignment !== null &&
  // assignment !== undefined` narrowing above into `onSubmit`, a function
  // declared further down this same render.
  const assignmentId = assignment.id;
  const isPhantomDoor = door.kind !== 'real';
  const outcomeOptions: OutcomeChoiceOption<VisitOutcome>[] = (
    Object.keys(OUTCOME_LABEL_KEYS) as VisitOutcome[]
  )
    // `phantom_absent_unit` ("Logement vide / n'existe pas") only makes
    // sense at a door the app itself is unsure is inhabited — never offered
    // at a real door (`docs/PHANTOM.md` §5).
    .filter((value) => value !== 'phantom_absent_unit' || isPhantomDoor)
    .map((value) => ({ value, label: t(OUTCOME_LABEL_KEYS[value]) }));
  const qualityRequired = outcome !== null && OUTCOMES_REQUIRING_QUALITY.has(outcome);
  const showDoNotVisit = outcome === 'refusal';

  async function communeOf(): Promise<string> {
    if (communeRef.current) return communeRef.current;
    const session = await getSession(sessionId);
    communeRef.current = session.commune;
    return session.commune;
  }

  async function onSubmit() {
    if (!outcome) {
      setFormError(t('household.sheet.outcomeRequired'));
      return;
    }
    if (qualityRequired && quality === null) {
      setFormError(t('household.sheet.qualityRequired'));
      return;
    }
    const anyEngagement = ENGAGEMENT_KINDS.some((kind) => engagements[kind].checked);
    if (anyEngagement && engagementFirstName.trim() === '') {
      setFormError(t('household.sheet.engagementFirstNameRequired'));
      return;
    }
    setFormError(null);
    setSubmitting(true);
    try {
      const visitResult = await recordVisit(assignmentId, {
        outcome,
        quality: qualityRequired ? quality : null,
        note,
        do_not_visit: showDoNotVisit ? doNotVisit : false,
      });
      // `'queued' in visitResult` is offline (or the immediate flush just
      // missed the network): the write is safe in the queue, but there is no
      // server id yet (`docs/OFFLINE.md` §2) — the volunteer moves on, the
      // door shows "en attente d'envoi" until the queue catches up.
      const visit = 'queued' in visitResult ? null : visitResult;
      if (!visit) {
        showToast(t('household.sheet.queuedVisit'), 'info');
      }

      const checkedKinds = ENGAGEMENT_KINDS.filter((kind) => engagements[kind].checked);
      if (checkedKinds.length > 0) {
        const commune = await communeOf();
        for (const kind of checkedKinds) {
          const draft = engagements[kind];
          await createEngagement({
            commune,
            address: kind === 'building_referent' ? door.address.id : null,
            first_name: engagementFirstName.trim(),
            email: kind === 'contact' ? draft.email : '',
            phone: kind === 'contact' ? draft.phone : '',
            kind,
            consent: true,
          });
        }
      }

      if (outcome === 'registration_info') {
        const registrationState: RegistrationDoorNavigationState = {
          doorId: door.id,
          doorLabel: door.label,
          addressLabel: door.address.label,
          ...(visit ? { visitId: visit.id } : {}),
          housingType: door.address.housing_type ?? 'unknown',
          confidence: door.confidence ?? null,
          hypothesis: door.hypothesis,
        };
        navigate(`/sessions/${sessionId}/door/registration`, { state: registrationState });
        return;
      }
      navigate(
        door.address.housing_type === 'building'
          ? `/sessions/${sessionId}/building`
          : `/sessions/${sessionId}`,
      );
    } catch (cause) {
      if (cause instanceof ApiError) {
        const body = cause.body as ApiErrorBody | undefined;
        setFormError(body?.detail ?? t('household.sheet.invalidVisit'));
      } else {
        setFormError(t('household.sheet.invalidVisit'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="household-screen">
      <div className="bloc">
        <div className="adr">
          {door.label} — {door.address.label}
        </div>
        <div className="badges">
          {door.building_name ? <Badge tone="violet">{door.building_name}</Badge> : null}
          {door.is_canvass_target ? (
            <Badge tone="red">{t('household.sheet.canvassTargetBadge')}</Badge>
          ) : null}
          {door.is_registration_target ? (
            <Badge tone="yellow">{t('household.sheet.registrationTargetBadge')}</Badge>
          ) : null}
        </div>
        <p className="kicker" style={{ marginTop: 'var(--space-3)' }}>
          {t('household.sheet.householdKicker', {
            count: door.household.length,
            plural: door.household.length === 1 ? '' : 's',
          })}
        </p>
        {isPhantomDoor ? (
          <div className="phantom-card">
            <p className="kicker" style={{ margin: '0 0 var(--space-2)' }}>
              {t('household.phantomCard.title')}
            </p>
            <PhantomBadge
              kind={door.kind}
              confidence={door.confidence}
              hypothesis={door.hypothesis}
            />
          </div>
        ) : (
          <HouseholdList members={door.household} />
        )}
        {door.last_visit ? (
          <p className="muted" style={{ marginTop: 'var(--space-2)' }}>
            {t('household.sheet.lastVisit', {
              date: formatShortDate(door.last_visit.recorded_at),
              outcome: door.last_visit.outcome,
              author: door.last_visit.by,
            })}
          </p>
        ) : null}
      </div>

      <div className="bloc">
        <p className="kicker">{t('household.sheet.outcomeKicker')}</p>
        <OutcomeChoice
          options={outcomeOptions}
          value={outcome}
          onChange={setOutcome}
          ariaLabel={t('household.sheet.outcomeKicker')}
          disabled={submitting}
        />

        {qualityRequired ? (
          <>
            <p className="kicker" style={{ marginTop: 'var(--space-4)' }}>
              {t('household.sheet.qualityKicker')}
            </p>
            <StarRating value={quality} onChange={setQuality} disabled={submitting} />
            <p className="muted" style={{ marginTop: 'var(--space-1)' }}>
              {t('household.sheet.qualityHint')}
            </p>
          </>
        ) : null}

        {showDoNotVisit ? (
          <div style={{ marginTop: 'var(--space-4)' }}>
            <Checkbox
              label={t('household.sheet.doNotVisit')}
              checked={doNotVisit}
              onChange={(event) => setDoNotVisit(event.target.checked)}
              disabled={submitting}
            />
          </div>
        ) : null}

        <p className="rappel" style={{ marginTop: 'var(--space-4)' }}>
          ⚠ {t('household.sheet.noteReminder')}
        </p>
        <Textarea
          label={t('household.sheet.noteLabel')}
          placeholder={t('household.sheet.notePlaceholder')}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          disabled={submitting}
          rows={2}
        />
      </div>

      {outcome !== 'phantom_absent_unit' ? (
        <div className="bloc">
          <p className="kicker">{t('household.sheet.engagementsKicker')}</p>
          <ConsentBox>{t('household.sheet.consent')}</ConsentBox>
          <div className="ui-field">
            <label className="ui-label" htmlFor="engagement-first-name">
              {t('household.sheet.engagementFirstName')}
            </label>
            <input
              id="engagement-first-name"
              className="ui-input"
              value={engagementFirstName}
              onChange={(event) => setEngagementFirstName(event.target.value)}
              disabled={submitting}
              autoComplete="off"
            />
          </div>
          <EngagementList value={engagements} onChange={setEngagements} disabled={submitting} />
        </div>
      ) : null}

      {formError ? (
        <div className="bloc">
          <p className="rappel" role="alert">
            {formError}
          </p>
        </div>
      ) : null}

      <div className="bloc">
        <Button variant="primary" fullWidth disabled={submitting} onClick={() => void onSubmit()}>
          {submitting ? t('household.sheet.submitting') : t('household.sheet.submit')}
        </Button>
      </div>
    </div>
  );
}
